import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { toast } from "@/hooks/use-toast";
import { Label } from "@/components/ui/label";
import { AuthLogo } from "@/components/auth/AuthLogo";
import { ArrowLeft } from "lucide-react";

const ForgotPassword = () => {
  const { t } = useTranslation();
  const { resetPassword } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const formSchema = z.object({
    email: z.string().email({ message: t("auth.emailInvalid") }),
  });

  type FormData = z.infer<typeof formSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    
    try {
      const { error } = await resetPassword(data.email);

      if (error) {
        toast({ title: error.message || t("auth.errorOccurred"), variant: "error" });
      } else {
        toast({ title: t("auth.resetEmailSent"), variant: "success" });
        setEmailSent(true);
      }
    } catch (error: any) {
      toast({ title: error.message || t("auth.errorOccurred"), variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <BrutalCard className="w-full max-w-md">
        <div className="text-center mb-8">
          <AuthLogo />
          <h1 className="font-heading font-bold text-3xl text-dark mb-2">
            {t("auth.resetPassword")}
          </h1>
          <p className="text-muted-foreground font-body">
            {emailSent
              ? t("auth.checkInbox")
              : "Inserisci la tua email per ricevere il link di reset"}
          </p>
        </div>

        {!emailSent ? (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="email" className="font-heading font-bold uppercase text-xs mb-2 block">
                {t("auth.email")}
              </Label>
              <BrutalInput
                id="email"
                type="email"
                {...register("email")}
                disabled={isLoading}
              />
              {errors.email && (
                <p className="text-destructive text-sm mt-1">{errors.email.message}</p>
              )}
            </div>

            <BrutalButton
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {t("auth.sendResetLink")}
            </BrutalButton>

            <Link to="/auth/login">
              <BrutalButton type="button" variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("auth.backToLogin")}
              </BrutalButton>
            </Link>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="bg-primary/10 border-[3px] border-primary p-4">
              <p className="font-body text-sm text-dark">
                {t("auth.verifyEmailMessage")}
              </p>
            </div>

            <Link to="/auth/login">
              <BrutalButton type="button" variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("auth.backToLogin")}
              </BrutalButton>
            </Link>
          </div>
        )}
      </BrutalCard>
    </div>
  );
};

export default ForgotPassword;
